import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBookOpen, FiBarChart2, FiTarget, FiTrendingUp, FiLayers, FiMap } = FiIcons;

const features = [
  {
    icon: FiBookOpen,
    number: '01',
    title: "The Exposure Methodology",
    desc: "Understand the exact 0 to 10 scale applied to 342 occupations across 143 million US jobs, and learn how to apply it to your specific plant."
  },
  {
    icon: FiBarChart2,
    number: '02',
    title: "All 19 M&R Roles Scored",
    desc: "Stop guessing. Get the definitive AI exposure scores for every role on your team, backed by official BLS data projections."
  },
  {
    icon: FiTarget,
    number: '03',
    title: "Task-Level AI Impact",
    desc: "A clear breakdown of which specific daily tasks, like PM generation, root cause analysis, and scheduling, will be fully automated."
  },
  {
    icon: FiTrendingUp,
    number: '04',
    title: "The 2026 Inflection Point",
    desc: "Discover the exact boundary that separates protected roles from exposed ones, and learn when the adoption curve will start to impact your team."
  },
  {
    icon: FiLayers,
    number: '05',
    title: "Strategic Headcount Guidance",
    desc: "Actionable blueprints to help maintenance leaders navigate workforce planning and budget allocation in the AI era."
  },
  {
    icon: FiMap,
    number: '06',
    title: "The 3-Phase Training Model",
    desc: "A structured upskilling approach designed to turn your at-risk knowledge workers into highly leveraged AI operators."
  }
];

const WhatsInside = () => {
  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        
        <div className="text-center mb-16">
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-sm font-bold text-jeruel-primary uppercase tracking-widest mb-3"
          >
            Why You Need This Data
          </motion.p>
          <motion.h2 
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-jeruel-dark"
          >
            Everything you need to <br className="hidden sm:block" />
            future-proof your workforce.
          </motion.h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-16">
          {features.map((feature, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.07 }}
              className="group relative bg-jeruel-offwhite hover:bg-jeruel-soft rounded-2xl p-8 border border-transparent hover:border-jeruel-primary/30 transition-all duration-300 cursor-default shadow-sm hover:shadow-md"
            >
              <div className="flex items-start gap-5">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white group-hover:bg-jeruel-primary border border-jeruel-gray group-hover:border-jeruel-primary flex items-center justify-center text-jeruel-primary group-hover:text-white transition-all duration-300 shadow-sm">
                  <SafeIcon icon={feature.icon} className="text-xl" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-jeruel-primary/50 group-hover:text-jeruel-primary/80 mb-1.5 transition-colors">{feature.number}</p>
                  <h4 className="text-lg font-bold text-jeruel-dark mb-2 leading-snug">{feature.title}</h4>
                  <p className="text-sm text-gray-600 font-medium leading-relaxed">{feature.desc}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-jeruel-primary rounded-2xl p-8 sm:p-12 text-center max-w-4xl mx-auto shadow-2xl relative overflow-hidden"
        >
          {/* Background Photographic Layer */}
          <img 
            src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=1200" 
            alt="Data and Planning" 
            className="absolute inset-0 w-full h-full object-cover mix-blend-multiply opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-jeruel-primary to-transparent opacity-80" />
          
          <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 relative z-10">
            Stop guessing about your team's future.
          </h3>
          <p className="text-blue-100 text-lg font-medium mb-8 max-w-2xl mx-auto relative z-10">
            The decisions you make about headcount and training today will dictate your plant's performance for the next decade. Base them on hard data.
          </p>
          
          <a 
            href="#download-form"
            className="inline-flex relative z-10 items-center gap-2 px-8 py-4 rounded-xl bg-white hover:bg-gray-50 text-jeruel-primary font-bold text-lg transition-all duration-300 shadow-xl hover:-translate-y-1"
          >
            Send Me the Free Report Now
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </motion.div>

      </div>
    </section>
  );
};

export default WhatsInside;